import { useMutation } from '@tanstack/react-query'
import { createApiCall } from 'services'

/**
 * Why a certificate did not move. Mirrors `BulkTransferFailureCode` on the server
 * (`bulk-transfer-certificates-to-owner.response.dto.ts`), which derives each value
 * from a distinct typed error thrown by the transfer use-case.
 *
 * This union is what lets the UI name a cause at all. It used to have one bucket for
 * everything the use-case refused, so an authorization failure and a stale status
 * were indistinguishable and both were reported as "their status changed — refresh".
 */
type BulkTransferFailureCode =
  | 'INVALID_STATUS'
  | 'NOT_PERMITTED'
  | 'NOT_FOUND'
  | 'UNKNOWN'
  | 'INTERNAL_ERROR'

/**
 * Per-certificate outcome. `skipped` means the server refused that certificate on
 * its own state (not `inactive`, or already mid-transfer); `failed` means the
 * attempt could not be made or broke. The distinction is what lets the caller report
 * a partial batch honestly instead of collapsing everything into "done".
 */
type BulkTransferResult = {
  id: string
  status: 'transferred' | 'skipped' | 'failed'
  code?: BulkTransferFailureCode
  /** Raw server reason. Diagnostic fallback only — see `FAILURE_COPY` in the modal. */
  message?: string
}

type BulkTransferResponse = {
  results: BulkTransferResult[]
  summary: {
    requested: number
    transferred: number
    skipped: number
    failed: number
  }
}

type UseCertificateBulkTransferOptions = {
  onSuccess?: (data?: BulkTransferResponse) => void
  onError?: (error?: any) => void
}

/**
 * N certificates -> ONE recipient, in a single request.
 *
 * Mirrors `useCertificateBulkActivate`. The loop lives on the SERVER
 * (`POST /transfer/admin/bulk-init`) rather than here on purpose: issuing N
 * requests from the client races the recipient's provisioning, because the
 * endpoint's `getOrCreateCustomerByEmail` is an unguarded read-then-write and
 * `owners.email` has no unique index. See the use-case's class comment.
 */
export const useCertificateBulkTransfer = (
  options?: UseCertificateBulkTransferOptions
) => {
  const mutation = useMutation({
    mutationFn: async (payload: { certificateIds: string[]; toCustomerEmail: string }) => {
      const response = await createApiCall({
        method: 'POST',
        url: `/transfer/admin/bulk-init`,
        data: payload,
      })
      return response.data as BulkTransferResponse
    },
    onSuccess: (data) => {
      options?.onSuccess?.(data)
    },
    onError: (error: any) => {
      options?.onError?.(error)
    },
  })

  const bulkTransfer = (payload: { certificateIds: string[]; toCustomerEmail: string }) => {
    mutation.mutate(payload)
  }

  return {
    bulkTransfer,
    isLoading: mutation.isPending,
    isError: mutation.isError,
  }
}

export type { BulkTransferResponse, BulkTransferResult, BulkTransferFailureCode }
